import { useEffect, useRef, useState, useCallback } from 'react'
import type { WebSocketEventType } from '../types'

interface WebSocketHookOptions {
  url?: string
  reconnectInterval?: number
  maxReconnectAttempts?: number
  onMessage?: (event: WebSocketEventType, data: unknown) => void
}

export interface WebSocketHookReturn {
  isConnected: boolean
  lastMessage: { event: WebSocketEventType; data: unknown } | null
  send: (message: string) => void
  subscribe: (event: WebSocketEventType, callback: (data: unknown) => void) => () => void
}

export function useWebSocket(options: WebSocketHookOptions = {}): WebSocketHookReturn {
  const {
    url = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/realtime`,
    reconnectInterval = 3000,
    maxReconnectAttempts = 10,
    onMessage,
  } = options

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimeoutRef = useRef<number | null>(null)
  const subscribersRef = useRef<Map<WebSocketEventType, Set<(data: unknown) => void>>>(new Map())

  const [isConnected, setIsConnected] = useState(false)
  const [lastMessage, setLastMessage] = useState<{ event: WebSocketEventType; data: unknown } | null>(null)

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return
    }

    try {
      wsRef.current = new WebSocket(url)

      wsRef.current.onopen = () => {
        setIsConnected(true)
        reconnectAttemptsRef.current = 0
        console.log('WebSocket connected')
      }

      wsRef.current.onclose = () => {
        setIsConnected(false)
        console.log('WebSocket disconnected')

        // Attempt to reconnect
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectTimeoutRef.current = window.setTimeout(() => {
            reconnectAttemptsRef.current++
            console.log(`Reconnecting... (attempt ${reconnectAttemptsRef.current})`)
            connect()
          }, reconnectInterval)
        }
      }

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error)
      }

      wsRef.current.onmessage = (messageEvent) => {
        try {
          const parsed = JSON.parse(messageEvent.data)
          const { event, data } = parsed as { event: WebSocketEventType; data: unknown }

          setLastMessage({ event, data })

          // Call global handler
          onMessage?.(event, data)

          // Call event-specific subscribers
          const eventSubscribers = subscribersRef.current.get(event)
          if (eventSubscribers) {
            eventSubscribers.forEach((callback) => callback(data))
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error)
        }
      }
    } catch (error) {
      console.error('Failed to create WebSocket:', error)
    }
  }, [url, reconnectInterval, maxReconnectAttempts, onMessage])

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
  }, [])

  const send = useCallback((message: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(message)
    } else {
      console.warn('WebSocket is not connected')
    }
  }, [])

  const subscribe = useCallback(
    (event: WebSocketEventType, callback: (data: unknown) => void) => {
      if (!subscribersRef.current.has(event)) {
        subscribersRef.current.set(event, new Set())
      }
      subscribersRef.current.get(event)!.add(callback)

      // Return unsubscribe function
      return () => {
        subscribersRef.current.get(event)?.delete(callback)
      }
    },
    []
  )

  // Start heartbeat to keep connection alive
  useEffect(() => {
    if (!isConnected) return

    const heartbeatInterval = setInterval(() => {
      send('heartbeat')
    }, 30000)

    return () => clearInterval(heartbeatInterval)
  }, [isConnected, send])

  // Connect on mount
  useEffect(() => {
    connect()
    return disconnect
  }, [connect, disconnect])

  return {
    isConnected,
    lastMessage,
    send,
    subscribe,
  }
}

// Hook for subscribing to specific WebSocket events
export function useWebSocketEvent<T = unknown>(
  ws: WebSocketHookReturn,
  event: WebSocketEventType,
  callback: (data: T) => void
) {
  useEffect(() => {
    return ws.subscribe(event, callback as (data: unknown) => void)
  }, [ws, event, callback])
}
